// 批次 19（B8-1，Gitea issue #14）：dashboard 頁型（main-* 首頁 8 變體）的資料層。
//
// dashboard 頁**不是契約驅動頁**——`contracts/pages/main-*.json` 全部 0 欄（KNOWN-NOT-DONE
// B8-1：dashboard 頁型與內容設定表是 E: 環境那條任務線的設計，本機無素材），結構與行為以
// legacy JSP＋JS 原始碼為基準（執行文件：docs/plans/2026-09-27-batch19-main-dashboard-plan.md）。
//
// legacy 讀取端點群（皆不在 endpoint-map，因此不走 MockDataSource.list）：
//   - `announcement/getAnnouncementsForMainPageAjax`（modal/announce.jsp:76，limitNum:0）
//   - `ajax/getCalendarEventsAjax`（modal/calendar.jsp:13，start/end ISO date）
//   - `main/ugy/student/getData?enquirerRole=`（mainUtils.js:422；＋studentId／planInstId），
//     `main/dentisit/student/getData`（mainUtils.js:452）、`main/ma/student/getData`（mainUtils.js:481）
//     → `resp.studentMainDTO`（JSON 字串再 parse）
//   - `form/getCoreSkillForm`（mainUtils.js:515）、`main/ugy/getStudentGPAFormReport`（:570）、
//     `main/ugy/getStudentEPAFormReport`（:593）→ S1/S2 加入
//   - todoForm：`queryMyTodoFormList`（commonMyFormList.jsp:90／modal/todoForm.jsp:111）→ S1 加入
//
// 【證據等級】§7 第 3 級（原始碼）。種子值是 legacy 資料庫內容的等價樣本——【雛形值待實機校準】。
// 持久化沿用批次 0 機制：key 掛在 `eportfolio-ui.mock-data.dashboard.*` 之下
// （`clearAllPersistedMockData()` 以 `eportfolio-ui.mock-data.` 前綴掃除，重置測試資料會一併清掉）。

import { MockDataError } from './mock-data-source';

// ---------------------------------------------------------------------------
// 型別（欄位名鏡像 legacy JS 的消費端：studentMain.js／mainUtils.js）
// ---------------------------------------------------------------------------

/** 資料家族＝legacy 取資料端點三分支（mainUtils.js:422/452/481）。 */
export type DashboardFamily = 'ugy' | 'dentisit' | 'ma';

/**
 * 公告列（mainUtils.js:62-77 renderAnnouncements）：
 * - `id`：`<a class="announcement" data-id>`（:69）
 * - `announcementId`：**legacy 已讀判定就是「這個欄位存在與否」**（:66
 *   `typeof announcement.announcement == "undefined"` → 未讀 span）。欄位名跟值語意不合
 *   是 legacy 原樣，照實保留（legacy-known-issue）。
 */
export interface DashboardAnnouncement {
  readonly id: string;
  readonly subject: string;
  readonly startFormatDate: string;
  readonly announcementId?: string;
}

/**
 * 行事曆事件（modal/calendar.jsp:10-27 → fullCalendar events；menu_Box_calendar_Tmpl:201
 * 用 backgroundColor/startTime/title；studentMain.js:299-323 renderCalendarMenu 以
 * `start > now && eventType != 'planInst'` 過濾）。
 * `eventType`：'schedule'（個人行程，legacy 註解「已拿掉」）／'class'／'meeting'／'planInst'。
 */
export interface DashboardCalendarEvent {
  readonly id: string;
  readonly title: string;
  readonly start: string;
  readonly backgroundColor: string;
  readonly eventType: 'schedule' | 'class' | 'meeting' | 'planInst';
}

/** 教師盒列（menu_Box_teacher_Tmpl:18：name＋extNo，data-id）。 */
export interface DashboardMenuTeacher {
  readonly id: string;
  readonly name: string;
  readonly extNo?: string;
}

/** 直立完成度盒（phase_Box_Tmpl:275；mainUtils.js:252 getPlanAndClassFormBox）。 */
export interface DashboardFormTargetBox {
  readonly completePercent: number;
  readonly totalQuantity: number;
  readonly displayText: string;
}

/** 等第制表單（mainUtils.js:284 getGPAFormBox；studentMain.js:257 GPAFormRow click；modal/gpaForms.jsp 欄位）。 */
export interface DashboardGpaForm {
  readonly isDone: boolean;
  readonly reviewStatus?: 'REVIEWING';
  readonly displayGrade: string;
  readonly formKey: string;
  readonly formTemplateId: string;
  readonly phaseTargetId: string;
  readonly formTargetId: string;
  readonly formTemplateName?: string;
  readonly startDate?: string;
  readonly dueDate?: string;
  readonly flowList?: readonly DashboardFlowNode[];
}

/** EPA 表單列（modal/epaForms.jsp 欄位；studentMain.js EPAFormRow click；MA 版加 isDone）。 */
export interface DashboardEpaForm {
  readonly formKey: string;
  readonly formTemplateId: string;
  readonly phaseTargetId: string;
  readonly formTargetId: string;
  readonly formTemplateName?: string;
  readonly displayGrade?: string;
  readonly startDate?: string;
  readonly dueDate?: string;
  readonly flowList?: readonly DashboardFlowNode[];
  /** MA 版（epaFormsForMA.jsp 表單狀態 欄：已完成/進行中）。 */
  readonly isDone?: boolean;
}

/** 階段列（phase_Tmpl:229＋studentMain.js:177 planFormRow click 的 phaseData）。 */
export interface DashboardPhase {
  readonly phaseTargetId: string;
  readonly phaseTargetName: string;
  readonly currentPhase: boolean;
  readonly showData: boolean;
  readonly phaseDeptId: string;
  readonly phaseDeptName: string;
  readonly phasePeriod: string;
  readonly phasePercent: number;
  readonly startDate: string;
  readonly endDate: string;
  readonly planForm: DashboardFormTargetBox;
  readonly classForm: DashboardFormTargetBox;
  readonly gpaForms: readonly DashboardGpaForm[];
  readonly epaForm: DashboardFormTargetBox;
  readonly epaForms: readonly DashboardEpaForm[];
  /** MA 版（renderMAStudentPlanPanel）：計畫表單 modal 的列（planFormsForMA.jsp 5 欄；
   *  medicalAffairs/studentMain.js:174 planForm click → showPlanFormsModal）。 */
  readonly planForms?: readonly DashboardMaPlanForm[];
  /** MA 版階段成績（getPhaseScoreBox mainUtils.js:262-276；medicalAffairs studentMain.js:179-192
   *  phaseScoreRow click → classFormList）。 */
  readonly phaseScore?: { readonly score: string };
}

/** MA 計畫表單 modal 列（planFormsForMA.jsp:16-67：formTemplateName 連結/startDate/dueDate/
 *  flowList/isDone 已完成|進行中；order [4 desc,2 asc]）。 */
export interface DashboardMaPlanForm {
  readonly formKey: string;
  readonly formTemplateId: string;
  readonly phaseTargetId: string;
  readonly formTargetId: string;
  readonly formTemplateName: string;
  readonly startDate: string;
  readonly dueDate: string;
  readonly flowList: DashboardFlowList;
  readonly isDone: boolean;
}

/** 待填/待批線上測驗列（commonMyExamList.jsp:10-52：學員第 4 欄＝應完成日 finishDateStr、
 *  教師/管理員＝學員姓名 studentName；試卷名稱連結 openTestingPage）。 */
export interface DashboardTodoExam {
  readonly id: string;
  readonly examTargetName: string;
  readonly examName: string;
  readonly teacherName: string;
  readonly finishDateStr: string;
  readonly studentName: string;
}

/** 待填學習紀錄列（commonMyStudyRecordList.jsp:18-49：planTargetName/genPhaseName/
 *  studentName 連結/studyRecordList li；click → studyRecordFormModalVar.showFill）。 */
export interface DashboardTodoStudyRecord {
  readonly id: string;
  readonly planTargetName: string;
  readonly phaseTargetId: string;
  readonly formKey: string;
  readonly studyRecordFormId: string;
  readonly studentName: string;
  /** ePortfolio.genPhaseName(row,true)（ePortfolio.js:2022-2032）：groupName/phaseName/subPhaseName。 */
  readonly groupName?: string;
  readonly phaseName?: string;
  readonly subPhaseName?: string;
  readonly studyRecordList: readonly { readonly studyItemName: string }[];
}

/**
 * 學員待辦&計畫資料（mainUtils.js:422 getStudentData → resp.studentMainDTO parse 後的形狀；

 * 消費端 studentMain.js:210-256 renderMenu／renderStudentCoreSkills／renderStudentPlanPanel）。
 */
export interface StudentMainDto {
  readonly planInst: {
    readonly studentId: string;
    readonly annualId: string;
    readonly organizationId: string;
    readonly jobTypeId: string;
    readonly planTargetId: string;
    /** 職類名稱（dentisit 版 mentorInterview/moodThermometer 的 formTemplateId 開關用；
     *  dentisit/studentMain.js:113-126：牙醫UGY→3050／牙醫PGY→3027／住院醫師→2265）。 */
    readonly jobTypeIdName?: string;
  };
  /** 五年級計畫 id 清單（臨床核心技能查詢用；studentMain.js:31）。 */
  readonly fifthPlanTargetIdList: readonly string[];
  /** 待辦事項盒（studentMain.js:225-274 renderMenu 的 key↔圖/文案開關表）。 */
  readonly todoItemMap: Readonly<
    Partial<
      Record<
        | 'ALL_FORM'
        | 'STUDY_RECORD'
        | 'EXAM'
        | 'AVAILABLE_COURSES'
        | 'LEAVE_FORM'
        | 'MENTOR_INTERVIEW'
        | 'MOOD_THERMOMETER',
        { readonly incompleteQuantity: number }
      >
    >
  >;
  readonly mentors: readonly DashboardMenuTeacher[];
  readonly clinicalTeachers: readonly DashboardMenuTeacher[];
  readonly coreSkillDTO?: {
    readonly completedQuantity: number;
    readonly totalQuantity: number;
    readonly completePercent: number;
    readonly deptCoreSkills: readonly {
      readonly displayText: string;
      readonly completePercent: number;
      readonly completedQuantity: number;
      readonly totalQuantity: number;
      readonly expansion1: string;
    }[];
  };
  readonly phaseList: readonly DashboardPhase[];
}

export interface DashboardData {
  readonly announcements: readonly DashboardAnnouncement[];
  readonly calendarEvents: readonly DashboardCalendarEvent[];
  readonly studentMainDto: Readonly<Record<DashboardFamily, StudentMainDto | null>>;
  /** 教師視角資料（main/ugy|dentisit|ma/teacher/getData → resp.teacherMainDTO；
   *  teacherMainUtils.js.gz:10-20）。 */
  readonly teacherMainDto: Readonly<Record<DashboardFamily, TeacherMainDto | null>>;
  /** 教師視角學員詳情（getStudentData(id,planInstId,teacherType)；teacherMain.js:99-105）。 */
  readonly studentDetails: Readonly<Record<DashboardFamily, readonly TeacherStudentDetail[]>>;
  /** MA 家族表單樣板（ajax/getFormTemplateListAjax purpose='plan' 的鏡像種子；
   *  medicalAffairs/studentMain.js:100-136 導生面談紀錄表 查找）。 */
  readonly maFormTemplates: readonly { readonly id: string; readonly name: string }[];
  /** 待批改/待填線上測驗（ajax/todo/getMyTodoExamListAjax；commonMyExamList.jsp:60-79）。 */
  readonly todoExams: readonly DashboardTodoExam[];
  /** 待填學習紀錄（training/studyRecord/getTodoStudyRecordListAjax isIncludeItem:true；
   *  commonMyStudyRecordList.jsp:63-79）。 */
  readonly todoStudyRecords: readonly DashboardTodoStudyRecord[];
  /** 等第制成績分布報表（main/ugy/getStudentGPAFormReport；studentGPAGrade.js:107-116）。 */
  readonly gpaFormReport: GpaFormReport;
  /** EPA成績分布報表（main/ugy/getStudentEPAFormReport；studentEpaGrade.js:132-139）。 */
  readonly epaFormReport: EpaFormReport;
  /** 首頁載入的留言數提醒（studentMain.jsp:39-46 `${discussBoardSize}` server 變數；>0 → alert）。 */
  readonly discussBoardSize: number;
  /** 公告內容（announcement/getAnnouncementAjax 回應 row；modal/announce.jsp:88-108 showAnnounce）。 */
  readonly announcementDetails: Readonly<Record<string, DashboardAnnouncementDetail>>;
  /** 待填表單（ajax/todo/getMyTodoListAjax 回應 list；modal/todoForm.jsp:111-127）。 */
  readonly todoForms: readonly DashboardTodoForm[];
  /** 臨床核心技能表單（form/getCoreSkillForm 回應 list；modal/coreSkillForms.jsp:50）。 */
  readonly coreSkillForms: readonly DashboardCoreSkillForm[];
  /** 個人行程明細（mySchedule/getMyScheduleAjax 回應 data；addCalendarModal.jsp:73-96）。 */
  readonly mySchedules: readonly DashboardMySchedule[];
  /** 行事曆上 class／meeting 事件對應的課程列（courseModal＝批次 11 CourseViewModal 的 row）。 */
  readonly courseRows: readonly DashboardRow[];
  // ── 批次 20：科部管理員首頁（main/ugy/deptAdmin/*；deptAdminMain.js）──
  /** 科部管理員首頁資料（main/ugy/deptAdmin/getData → resp.deptAdminMainDTO 字串再 parse；
   *  deptAdminMain.js:533-548 getDeptAdminData）。 */
  readonly deptAdminMainDto: DeptAdminMainDto;
  /** 計畫資料（main/ugy/deptAdmin/plan?planTargetId= → resp.dto；deptAdminMain.js:426-450 queryPlanInfo；
   *  key＝planTargetId）。 */
  readonly deptAdminPlans: Readonly<Record<string, DeptAdminPlanDto>>;
  /** 隨段學員臨床教師清單（main/ugy/deptAdmin/clinicalList → resp.phaseList；deptAdminMain.js:632-649；
   *  key＝phaseTargetId）。 */
  readonly deptAdminClinicalLists: Readonly<Record<string, readonly DeptAdminClinicalRow[]>>;
  /** 部門清單（ajax/getDepartmentListByJobTypeAjax → resp.list；deptAdminMain.js:325-346，同步 AJAX）。 */
  readonly deptAdminDepartments: readonly { readonly id: string; readonly name: string }[];
  /** 部門帳號清單（ajax/getAccountListByDepartmentIdNewAjax → resp.list；ePortfolio.js:1752-1770；
   *  key＝departmentId）。 */
  readonly deptAdminAccounts: Readonly<Record<string, readonly { readonly id: string; readonly name: string }[]>>;
  /** 登入帳號所屬部門（layout server 變數 loginAccountDepartmentId；dept select 預設值，
   *  deptAdminMain.js:379 `.val(loginAccountDepartmentId).trigger("change")`）。 */
  readonly loginAccountDepartmentId: string;
}

/**
 * 公告內容（modal/announce.jsp:88-108 showAnnounce：annoDate＝startDateStr ~ endDateStr、
 * dialogModalLabel＝subject、annoRefName＝refName、annoObjName＝roleNames、url（空＝無）、
 * content（XSSUtils.sanitizeHtml 後 render））。
 */
export interface DashboardAnnouncementDetail {
  readonly id: string;
  readonly subject: string;
  readonly startDateStr: string;
  readonly endDateStr: string;
  readonly refName: string;
  readonly roleNames: string;
  readonly url: string;
  readonly content: string;
}

/**
 * 待填表單列（modal/todoForm.jsp:16-88 五欄：formTemplateName（＋formTargetTypeName、
 * ＋號換空格）／訓練計畫|課程（kind=leave|refTable=CoEditForm|studyRecord|examList 提示
 * 字串，其餘＝className|planName（＋phaseName））／studentName／startDate／dueDate；
 * 列點擊 :90-110 依 kind/refTable 分流）。
 */
export interface DashboardTodoForm {
  readonly id: string;
  readonly formTemplateName: string;
  readonly formTargetTypeName?: string;
  readonly kind?: 'leave';
  readonly refTable: string;
  readonly refId: string;
  readonly formKey: string;
  readonly formTemplateId: string;
  readonly formTargetId: string;
  readonly studentName: string;
  readonly startDate: string;
  readonly dueDate: string;
  readonly className?: string;
  readonly planName?: string;
  readonly phaseName?: string;
}

/** 表單流程節點（mainUtils.js:32 genFormFlow 消費端；done／added／flowRoleName／accountName）。 */
export interface DashboardFlowNode {
  readonly done: boolean;
  readonly added?: boolean;
  readonly flowRoleName: string;
  readonly accountName?: string;
}

/**
 * 臨床核心技能表單列（modal/coreSkillForms.jsp:50-103：formTemplateName（id>0 才連結）/
 * 簡核狀態（第一個未完成 flow 的 accountName）/表單流程（genFormFlow）/表單狀態
 * （displayGrade）/排序分數（score 隐藏欄，order asc））。
 */
export interface DashboardCoreSkillForm {
  readonly id: number;
  readonly formTemplateName: string;
  readonly isDone: boolean;
  readonly flowList: readonly DashboardFlowNode[];
  readonly displayGrade: string;
  readonly score: number;
  readonly formKey: string;
  readonly formTemplateId: string;
  readonly phaseTargetId: string;
  readonly formTargetId: string;
}

/** 個人行程明細（addCalendarModal.jsp:73-96 showScheduleModal 預填欄位）。 */
export interface DashboardMySchedule {
  readonly id: string;
  readonly content: string;
  readonly scheduleStartDateString: string;
  readonly scheduleEndDateString: string;
  readonly memo: string;
  readonly groupKey: string;
  /** 待決定邀請（approveOrReject 的婉拒/接受鈕；addCalendarModal.jsp:364-366 notDecide class）。 */
  readonly isDecide?: boolean;
}

/** 課程列（CourseViewModal 的 row；行事曆 class/meeting 事件點擊用）。 */
export type DashboardRow = Record<string, unknown>;

// ---------------------------------------------------------------------------
// 教師視角（S2；teacherMainUtils.js.gz＋teacherMain.js）
// ---------------------------------------------------------------------------

/** 學員名單列（menu_Box_studentInfo_Tmpl:185-192：data-id/data-planInstId＋name＋extNo；
 *  expansion1＝年級 C1/C2，teacherMain.js genStudentsByGrade :229 以之過濾）。 */
export interface DashboardStudentListItem {
  readonly id: string;
  readonly name: string;
  readonly extNo?: string;
  readonly planInstId: string;
  readonly expansion1: 'C1' | 'C2';
}

/** 教師首頁資料（main/ugy|dentisit|ma/teacher/getData → resp.teacherMainDTO）。 */
export interface TeacherMainDto {
  /** 職類名稱（dentisit/teacherMain.js:66-83 的 formTemplateId 開關用：
   *  牙醫UGY→3050／牙醫PGY→3027／住院醫師→2263【教師版與學員版 2265 不同】）。 */
  readonly jobTypeIdName?: string;
  readonly todoItemMap: Readonly<Partial<Record<'ALL_FORM' | 'MENTOR_INTERVIEW', { readonly incompleteQuantity: number }>>>;
  readonly mentorStudents: readonly DashboardStudentListItem[];
  readonly clinicalStudents: readonly DashboardStudentListItem[];
  readonly pastClinicalStudents: readonly DashboardStudentListItem[];
}

/**
 * 教師視角學員詳情（getStudentData 回傳 studentMainDTO；teacherMain.js:99-105）——
 * 學員版形狀＋學員資訊面板/燈號/GPA·EPA modal 用的額外欄位
 * （studentName/grade/gpaLightColor/epaLightColor/gpaFailDeptNames/epaFailDeptNames/
 * difficulty/difficulyReason，teacherMain.js:251-293 bindGradeTooltipster）。
 */
export interface TeacherStudentDetail extends StudentMainDto {
  readonly studentName: string;
  readonly grade: 'C1' | 'C2';
  /** 燈號顏色 class（gpaLight/epaLight；green/yellow/red）。 */
  readonly gpaLightColor: 'green' | 'yellow' | 'red';
  readonly epaLightColor: 'green' | 'yellow' | 'red';
  readonly gpaFailDeptNames?: readonly string[];
  readonly epaFailDeptNames?: readonly string[];
  readonly difficulty?: boolean;
  readonly difficulyReason?: string;
}

/** 表單流程節點串（mainUtils.js:32 genFormFlow 的消費形狀；dashboard 各 modal 共用）。 */
export type DashboardFlowList = readonly {
  done: boolean;
  added?: boolean;
  flowRoleName: string;
  accountName?: string;
}[];

/** 等第制報表表單列（studentGPAGrade.js:129-172 的 9 欄形狀）。 */
export interface DashboardGradeReportRow {
  readonly formTemplateName: string;
  readonly planTargetName: string;
  readonly groupName?: string;
  readonly subPhaseName: string;
  readonly phaseName?: string;
  readonly phaseDepartmentName: string;
  readonly phaseStartDate: string;
  readonly phaseEndDate: string;
  readonly dueDate: string;
  readonly flowList: DashboardFlowList;
  readonly displayGrade: string;
  readonly formKey: string;
  readonly formTemplateId: string;
  readonly phaseTargetId: string;
  readonly formTargetId: string;
}

/** 等第制成績分布報表（studentGPAGrade.js:119-127 rednerGPAReport）。 */
export interface GpaFormReport {
  readonly gradeCountMap: Readonly<Record<'D' | 'C' | 'B' | 'A-' | 'A' | 'A+' | 'A++', number>>;
  readonly avgScore?: string;
  readonly allStudentAvgScore?: string;
  readonly gpaForms: readonly DashboardGradeReportRow[];
}

/** EPA 分數分布豆（studentEpaGrade.js:170-213）。 */
export interface DashboardEpaGradeBean {
  readonly scoreCountMap: Readonly<Partial<Record<'1.0' | '2.0' | '3.0' | '4.0' | '5.0', number>>>;
  readonly testingCount: number;
  readonly studentAvgScore?: number;
  readonly avgScore: number;
}

/** EPA 成績分布報表（studentEpaGrade.js:143-167 renderEPAForms/renderEPAScoreRow）。 */
export interface EpaFormReport {
  readonly epaGradeMap: Readonly<Record<string, DashboardEpaGradeBean>>;
  readonly epaTitleMap: Readonly<Record<string, string>>;
  readonly epaForms: readonly {
    readonly form: Omit<DashboardGradeReportRow, 'displayGrade'> & { readonly displayGrade: string };
    readonly epaScoreMap: Readonly<Record<string, number>>;
  }[];
}

// ---------------------------------------------------------------------------
// 批次 20：科部管理員首頁（main/ugy/deptAdmin/*；deptAdminMain.js）
// ---------------------------------------------------------------------------

/** 科部管理員首頁資料（main/ugy/deptAdmin/getData → resp.deptAdminMainDTO parse 後形狀；消費端
 *  deptAdminMain.js:553-581 renderMenu：**switch 只處理 ALL_FORM**（其他 key 照渲染但無 class＝
 *  不可點）＋planList_Box_Tmpl 訓練計畫盒）。 */
export interface DeptAdminMainDto {
  readonly todoItemMap: Readonly<Partial<Record<'ALL_FORM', { readonly incompleteQuantity: number }>>>;
  /** 計畫清單盒的列（planList_Box_Tmpl:480-496：plan-item data-id＋plan-name）。 */
  readonly planTargetList: readonly { readonly id: string; readonly name: string }[];
}

/** 計畫面板的隨段列（queryPlanInfo → dto.phaseList；deptAdminMain.js:466-510 renderPhaseList 消費端）。 */
export interface DeptAdminPhase {
  readonly phaseTargetId: string;
  readonly phaseTargetName: string;
  readonly phaseDeptId: string;
  readonly phaseDeptName: string;
  readonly phasePeriod: string;
  /** 起訖日（deptAdminMain.js:105-106/:121-122 defaultStartDate/End 消費端）。 */
  readonly startDate: string;
  readonly endDate: string;
  readonly phasePercent: number;
  readonly currentPhase: boolean;
  readonly showData: boolean;
  /** 計畫表單欄（getPlanAndClassFormBox(phase.planForm)，deptAdminMain.js:488）。 */
  readonly planForm: DashboardFormTargetBox;
  /** 課程表單欄（getPlanAndClassFormBox(phase.classAndMeetingForm)，deptAdminMain.js:492）。 */
  readonly classAndMeetingForm: DashboardFormTargetBox;
  /** 教師安排欄（getClinicalArrangementBox(phase.clinicalArrangement)，deptAdminMain.js:496；
   *  displayText＝「已安排/應安排」）。 */
  readonly clinicalArrangement: DashboardFormTargetBox;
}

/** 計畫資料（main/ugy/deptAdmin/plan?planTargetId= → resp.dto；deptAdminMain.js:426-450 queryPlanInfo；
 *  舊面板查找用 dto.planTargetId（deptAdminMain.js:468）、面板 data-id＝planTarget.id（mainPageTemplate.jsp:498-499）。 */
export interface DeptAdminPlanDto {
  readonly planTargetId: string;
  readonly planTarget: {
    readonly id: string;
    readonly name: string;
    readonly annualId: string;
    readonly organizationId: string;
    readonly jobTypeId: string;
    /** 臨床教師欄位數（1~3；決定 modal 第 4 欄顯隱＋儲存驗證拼接，deptAdminMain.js:133-149/:256-302）。 */
    readonly clinicalAmount: number;
  };
  readonly phaseList: readonly DeptAdminPhase[];
}

/** 隨段學員臨床教師列（main/ugy/deptAdmin/clinicalList → resp.phaseList；phase_clinical_tmpl:544-575。
 *  clinical1Id/2Id/3Id＝帳號 id（數值；<=0＝未安排，renderPhaseClinicalTable :337-349 會刪欄）。 */
export interface DeptAdminClinicalRow {
  readonly phaseInstId: string;
  readonly studentName: string;
  readonly studentEmpId: string;
  readonly clinical1Id: number;
  readonly clinical2Id: number;
  readonly clinical3Id: number;
}

// ---------------------------------------------------------------------------
// 種子（buildDefaultDashboardData）
// ---------------------------------------------------------------------------

/** 公告日期基準（startFormatDate 在 legacy 是 server 格式化字串，種子直接給字面值）。 */
function isoDate(base: Date, offsetDays: number): string {
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + offsetDays, 8, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:00`;
}

/**
 * 預設種子。行事曆事件以「今天」為基準取未來日期（menu 盒只顯示 `start > now` 的行程，
 * studentMain.js:309；種子含一筆 planInst 與一筆過去事件來覆蓋過濾規則的兩個分支）。
 */
export function buildDefaultDashboardData(now: Date): DashboardData {
  return {
    // 首頁載入留言提醒（studentMain.jsp:39-46；>0 → alert）。
    discussBoardSize: 2,
    // 公告欄（mainUtils.js:62-77：未讀＝無 announcementId 欄位；時間＝startFormatDate）。
    announcements: [
      { id: '1', subject: '系統維護公告（09/28 22:00-24:00）', startFormatDate: '2026/09/25', announcementId: '1' },
      { id: '2', subject: '115 年度第一梯次實習說明會', startFormatDate: '2026/09/24' },
      { id: '3', subject: '線上測驗題庫更新通知', startFormatDate: '2026/09/20', announcementId: '3' },
      { id: '4', subject: '教學檢討會議程預告', startFormatDate: '2026/09/18' },
    ],
    calendarEvents: [
      { id: 'c1', title: '內科晨會', start: isoDate(now, 1), backgroundColor: '#3a87ad', eventType: 'class' },
      { id: 'c2', title: '教學檢討會', start: isoDate(now, 2), backgroundColor: '#aa7d39', eventType: 'meeting' },
      { id: 'c3', title: '個人行程：圖書館', start: isoDate(now, 1), backgroundColor: '#6aa84f', eventType: 'schedule' },
      { id: 'c4', title: '訓練期程：第二階段', start: isoDate(now, 5), backgroundColor: '#999999', eventType: 'planInst' },
      { id: 'c5', title: '已過去的行程', start: isoDate(now, -1), backgroundColor: '#3a87ad', eventType: 'class' },
    ],
    studentMainDto: {
      // ── 一般醫學（ugy）家族：studentMain.js 的消費形狀 ──
      ugy: {
        planInst: {
          studentId: 'S0001',
          annualId: '1',
          organizationId: '1',
          jobTypeId: '1',
          planTargetId: '1',
        },
        fifthPlanTargetIdList: ['101', '102'],
        todoItemMap: {
          ALL_FORM: { incompleteQuantity: 3 },
          STUDY_RECORD: { incompleteQuantity: 2 },
          EXAM: { incompleteQuantity: 1 },
          AVAILABLE_COURSES: { incompleteQuantity: 4 },
          LEAVE_FORM: { incompleteQuantity: 0 },
          MENTOR_INTERVIEW: { incompleteQuantity: 1 },
          MOOD_THERMOMETER: { incompleteQuantity: 1 },
        },
        mentors: [{ id: 'T0001', name: '王大同', extNo: '2251' }],
        clinicalTeachers: [
          { id: 'T0002', name: '李大明', extNo: '2252' },
          { id: 'T0003', name: '陳中明', extNo: '2253' },
        ],
        coreSkillDTO: {
          completedQuantity: 5,
          totalQuantity: 10,
          completePercent: 50,
          deptCoreSkills: [
            { displayText: '內科', completePercent: 40, completedQuantity: 2, totalQuantity: 5, expansion1: 'D01' },
            { displayText: '外科', completePercent: 60, completedQuantity: 3, totalQuantity: 5, expansion1: 'D02' },
          ],
        },
        phaseList: [
          {
            phaseTargetId: '101',
            phaseTargetName: '第一階段（內科）',
            currentPhase: false,
            showData: true,
            phaseDeptId: 'D01',
            phaseDeptName: '內科部',
            phasePeriod: '2026/01 - 2026/03',
            phasePercent: 100,
            startDate: '2026-01-01',
            endDate: '2026-03-31',
            planForm: { completePercent: 100, totalQuantity: 2, displayText: '2/2' },
            classForm: { completePercent: 100, totalQuantity: 3, displayText: '3/3' },
            gpaForms: [
              {
                isDone: true,
                displayGrade: 'A',
                formKey: 'gpaForm',
                formTemplateId: '3001',
                phaseTargetId: '101',
                formTargetId: '5001',
                formTemplateName: 'GPA 評量表單（第一階段）',
                startDate: '2026/01/01',
                dueDate: '2026/03/31',
                flowList: [
                  { done: true, flowRoleName: '實習單位臨床教師', accountName: '李大明' },
                  { done: true, flowRoleName: '導師', accountName: '王大同' },
                ],
              },
            ],
            epaForm: { completePercent: 100, totalQuantity: 2, displayText: '2/2' },
            epaForms: [
              {
                formKey: 'epaForm',
                formTemplateId: '6001',
                phaseTargetId: '101',
                formTargetId: '6001',
                formTemplateName: 'EPA 評估表單（第一階段）',
                displayGrade: '4',
                startDate: '2026/01/01',
                dueDate: '2026/03/31',
                flowList: [{ done: true, flowRoleName: '實習單位臨床教師', accountName: '李大明' }],
              },
            ],
          },
          {
            phaseTargetId: '102',
            phaseTargetName: '第二階段（外科）',
            currentPhase: true,
            showData: true,
            phaseDeptId: 'D02',
            phaseDeptName: '外科部',
            phasePeriod: '2026/04 - 2026/09',
            phasePercent: 60,
            startDate: '2026-04-01',
            endDate: '2026-09-30',
            planForm: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
            classForm: { completePercent: 33, totalQuantity: 3, displayText: '1/3' },
            gpaForms: [
              {
                isDone: false,
                reviewStatus: 'REVIEWING',
                displayGrade: '-',
                formKey: 'gpaForm',
                formTemplateId: '3001',
                phaseTargetId: '102',
                formTargetId: '5002',
                formTemplateName: 'GPA 評量表單（第二階段）',
                startDate: '2026/04/01',
                dueDate: '2026/09/30',
                flowList: [{ done: false, flowRoleName: '實習單位臨床教師', accountName: '陳中明' }],
              },
            ],
            epaForm: { completePercent: 25, totalQuantity: 4, displayText: '1/4' },
            epaForms: [
              {
                formKey: 'epaForm',
                formTemplateId: '6002',
                phaseTargetId: '102',
                formTargetId: '6002',
                formTemplateName: 'EPA 評估表單（第二階段）',
                displayGrade: '',
                startDate: '2026/04/01',
                dueDate: '2026/09/30',
                flowList: [{ done: false, flowRoleName: '實習單位臨床教師', accountName: '陳中明' }],
              },
            ],
          },
          {
            phaseTargetId: '103',
            phaseTargetName: '第三階段（急診）',
            currentPhase: false,
            showData: false,
            phaseDeptId: 'D03',
            phaseDeptName: '急診醫學部',
            phasePeriod: '2026/10 - 2026/12',
            phasePercent: 0,
            startDate: '2026-10-01',
            endDate: '2026-12-31',
            planForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            classForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            gpaForms: [],
            epaForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            epaForms: [],
          },
        ],
      },
      // ── 牙醫（dentisit）家族：S3——7 待辦 key＋substage 3 欄（無等第制/EPA 欄）；
      //    planInst.jobTypeIdName＝dentisit/studentMain.js:113-126 的開關來源 ──
      dentisit: {
        planInst: {
          studentId: 'S0002',
          annualId: '1',
          organizationId: '2',
          jobTypeId: '2',
          planTargetId: '2',
          jobTypeIdName: '牙醫UGY',
        },
        fifthPlanTargetIdList: [],
        todoItemMap: {
          ALL_FORM: { incompleteQuantity: 1 },
          STUDY_RECORD: { incompleteQuantity: 2 },
          EXAM: { incompleteQuantity: 1 },
          AVAILABLE_COURSES: { incompleteQuantity: 2 },
          LEAVE_FORM: { incompleteQuantity: 0 },
          MENTOR_INTERVIEW: { incompleteQuantity: 1 },
          MOOD_THERMOMETER: { incompleteQuantity: 1 },
        },
        mentors: [{ id: 'T0011', name: '牙醫導師', extNo: '2261' }],
        clinicalTeachers: [{ id: 'T0012', name: '牙醫臨床教師', extNo: '2262' }],
        phaseList: [
          {
            phaseTargetId: '201',
            phaseTargetName: '牙醫第一階段（補經物）',
            currentPhase: true,
            showData: true,
            phaseDeptId: 'D11',
            phaseDeptName: '牙體復原科',
            phasePeriod: '2026/01 - 2026/06',
            phasePercent: 50,
            startDate: '2026-01-01',
            endDate: '2026-06-30',
            planForm: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
            classForm: { completePercent: 33, totalQuantity: 3, displayText: '1/3' },
            gpaForms: [],
            epaForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            epaForms: [],
          },
          {
            phaseTargetId: '202',
            phaseTargetName: '牙醫第二階段（口腔外科）',
            currentPhase: false,
            showData: false,
            phaseDeptId: 'D12',
            phaseDeptName: '口腔顎面外科',
            phasePeriod: '2026/07 - 2026/12',
            phasePercent: 0,
            startDate: '2026-07-01',
            endDate: '2026-12-31',
            planForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            classForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            gpaForms: [],
            epaForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            epaForms: [],
          },
        ],
      },
      // ── 醫事職類（ma）家族：S4——7 待辦 key（LEAVE_FORM 盒有渲染但無 click handler＝legacy）、
      //    ma 4 欄（訓練階段/計畫表單/階段成績/EPAs）＋planForms modal＋表單樣板查找 ──
      ma: {
        planInst: {
          studentId: 'S0003',
          annualId: '1',
          organizationId: '3',
          jobTypeId: '3',
          planTargetId: '3',
        },
        fifthPlanTargetIdList: [],
        todoItemMap: {
          ALL_FORM: { incompleteQuantity: 2 },
          STUDY_RECORD: { incompleteQuantity: 1 },
          EXAM: { incompleteQuantity: 1 },
          AVAILABLE_COURSES: { incompleteQuantity: 1 },
          LEAVE_FORM: { incompleteQuantity: 1 },
          MENTOR_INTERVIEW: { incompleteQuantity: 1 },
          MOOD_THERMOMETER: { incompleteQuantity: 1 },
        },
        mentors: [{ id: 'T0021', name: '醫事導師', extNo: '2271' }],
        clinicalTeachers: [{ id: 'T0022', name: '醫事臨床教師', extNo: '2272' }],
        phaseList: [
          {
            phaseTargetId: '301',
            phaseTargetName: '醫事第一階段（護理）',
            currentPhase: true,
            showData: true,
            phaseDeptId: 'D21',
            phaseDeptName: '護理部',
            phasePeriod: '2026/01 - 2026/06',
            phasePercent: 50,
            startDate: '2026-01-01',
            endDate: '2026-06-30',
            planForm: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
            classForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            gpaForms: [],
            epaForm: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
            epaForms: [
              {
                formKey: 'epaForm',
                formTemplateId: '6101',
                phaseTargetId: '301',
                formTargetId: '6101',
                formTemplateName: 'EPA 評估表單（護理 第一階段）',
                startDate: '2026/01/01',
                dueDate: '2026/06/30',
                flowList: [{ done: true, flowRoleName: '實習單位臨床教師', accountName: '醫事臨床教師' }],
                isDone: true,
              },
            ],
            planForms: [
              {
                formKey: 'planForm',
                formTemplateId: '3201',
                phaseTargetId: '301',
                formTargetId: '5201',
                formTemplateName: '計畫表單（護理 第一階段）',
                startDate: '2026/01/01',
                dueDate: '2026/06/30',
                flowList: [{ done: true, flowRoleName: '導師', accountName: '醫事導師' }],
                isDone: true,
              },
              {
                formKey: 'planForm',
                formTemplateId: '3202',
                phaseTargetId: '301',
                formTargetId: '5202',
                formTemplateName: '計畫表單（護理 加會）',
                startDate: '2026/03/01',
                dueDate: '2026/05/31',
                flowList: [{ done: false, added: true, flowRoleName: '導師', accountName: '' }],
                isDone: false,
              },
            ],
            phaseScore: { score: '82' },
          },
        ],
      },
    },
    // MA 表單樣板（ajax/getFormTemplateListAjax purpose='plan' 鏡像；導生面談紀錄表 查找）。
    maFormTemplates: [
      { id: '3401', name: '導生面談紀錄表' },
      { id: '3402', name: '心情溫度計量表' },
    ],
    // 待批改/待填線上測驗（ajax/todo/getMyTodoExamListAjax；commonMyExamList.jsp:60-79）。
    todoExams: [
      {
        id: '9001',
        examTargetName: 'UGY 計畫／第一階段（內科）',
        examName: '內科核心知識測驗 A 卷',
        teacherName: '王大同',
        finishDateStr: '2026/09/30',
        studentName: '林同學',
      },
      {
        id: '9002',
        examTargetName: 'UGY 計畫／第二階段（外科）',
        examName: '外科傷口處理測驗',
        teacherName: '李大明',
        finishDateStr: '2026/10/05',
        studentName: '林同學',
      },
    ],
    // 待填學習紀錄（training/studyRecord/getTodoStudyRecordListAjax；commonMyStudyRecordList.jsp:63-79）。
    todoStudyRecords: [
      {
        id: '8001',
        planTargetName: 'UGY 計畫',
        phaseTargetId: '102',
        formKey: 'studyRecord',
        studyRecordFormId: '8101',
        studentName: '林同學',
        groupName: 'A 組',
        phaseName: '第二階段',
        subPhaseName: '外科次階段',
        studyRecordList: [
          { studyItemName: '病歷寫作（Admission note）' },
          { studyItemName: 'Mini-CEX 一次' },
        ],
      },
    ],
    // ── 教師視角（S2；main/ugy/teacher/getData → teacherMainDTO）──
    teacherMainDto: {
      ugy: {
        todoItemMap: {
          ALL_FORM: { incompleteQuantity: 2 },
          MENTOR_INTERVIEW: { incompleteQuantity: 1 },
        },
        mentorStudents: [
          { id: 'S0001', name: '林同學', extNo: '2601', planInstId: '9101', expansion1: 'C1' },
          { id: 'S0002', name: '王同學', extNo: '2602', planInstId: '9102', expansion1: 'C2' },
        ],
        clinicalStudents: [
          { id: 'S0001', name: '林同學', extNo: '2601', planInstId: '9201', expansion1: 'C1' },
          { id: 'S0003', name: '張同學', extNo: '2603', planInstId: '9202', expansion1: 'C2' },
        ],
        pastClinicalStudents: [
          { id: 'S0004', name: '陳同學（上學年）', extNo: '2604', planInstId: '9203', expansion1: 'C1' },
        ],
      },
      // ── 牙醫/醫事家族：S3/S4 補足值 ──
      dentisit: {
        jobTypeIdName: '牙醫UGY',
        todoItemMap: { ALL_FORM: { incompleteQuantity: 1 }, MENTOR_INTERVIEW: { incompleteQuantity: 1 } },
        mentorStudents: [
          { id: 'S0005', name: '牙醫學員', extNo: '2701', planInstId: '9301', expansion1: 'C1' },
          { id: 'S0007', name: '牙醫學員二', extNo: '2702', planInstId: '9302', expansion1: 'C2' },
        ],
        clinicalStudents: [
          { id: 'S0005', name: '牙醫學員', extNo: '2701', planInstId: '9303', expansion1: 'C1' },
        ],
        pastClinicalStudents: [
          { id: 'S0008', name: '牙醫畢業學員', extNo: '2703', planInstId: '9304', expansion1: 'C1' },
        ],
      },
      ma: {
        jobTypeIdName: '醫事職類',
        todoItemMap: { ALL_FORM: { incompleteQuantity: 1 }, MENTOR_INTERVIEW: { incompleteQuantity: 1 } },
        mentorStudents: [{ id: 'S0006', name: '醫事學員', extNo: '2801', planInstId: '9401', expansion1: 'C1' }],
        clinicalStudents: [],
        pastClinicalStudents: [],
      },
    },
    // 教師視角學員詳情（getStudentData(id,planInstId,teacherType)；studentMainDTO＋燈號欄）。
    studentDetails: {
      ugy: [
        {
          planInst: { studentId: 'S0001', annualId: '1', organizationId: '1', jobTypeId: '1', planTargetId: '1' },
          fifthPlanTargetIdList: ['101', '102'],
          todoItemMap: {},
          mentors: [],
          clinicalTeachers: [],
          coreSkillDTO: {
            completedQuantity: 5,
            totalQuantity: 10,
            completePercent: 50,
            deptCoreSkills: [
              { displayText: '內科', completePercent: 40, completedQuantity: 2, totalQuantity: 5, expansion1: 'D01' },
            ],
          },
          phaseList: [
            {
              phaseTargetId: '101',
              phaseTargetName: '第一階段（內科）',
              currentPhase: false,
              showData: true,
              phaseDeptId: 'D01',
              phaseDeptName: '內科部',
              phasePeriod: '2026/01 - 2026/03',
              phasePercent: 100,
              startDate: '2026-01-01',
              endDate: '2026-03-31',
              planForm: { completePercent: 100, totalQuantity: 2, displayText: '2/2' },
              classForm: { completePercent: 100, totalQuantity: 3, displayText: '3/3' },
              gpaForms: [
                {
                  isDone: true,
                  displayGrade: 'B',
                  formKey: 'gpaForm',
                  formTemplateId: '3001',
                  phaseTargetId: '101',
                  formTargetId: '5001',
                  formTemplateName: 'GPA 評量表單（第一階段）',
                  startDate: '2026/01/01',
                  dueDate: '2026/03/31',
                  flowList: [{ done: true, flowRoleName: '實習單位臨床教師', accountName: '李大明' }],
                },
              ],
              epaForm: { completePercent: 100, totalQuantity: 2, displayText: '2/2' },
              epaForms: [
                {
                  formKey: 'epaForm',
                  formTemplateId: '6001',
                  phaseTargetId: '101',
                  formTargetId: '6001',
                  formTemplateName: 'EPA 評估表單（第一階段）',
                  displayGrade: '2',
                  startDate: '2026/01/01',
                  dueDate: '2026/03/31',
                  flowList: [{ done: true, flowRoleName: '實習單位臨床教師', accountName: '李大明' }],
                },
              ],
            },
            {
              phaseTargetId: '102',
              phaseTargetName: '第二階段（外科）',
              currentPhase: true,
              showData: true,
              phaseDeptId: 'D02',
              phaseDeptName: '外科部',
              phasePeriod: '2026/04 - 2026/09',
              phasePercent: 60,
              startDate: '2026-04-01',
              endDate: '2026-09-30',
              planForm: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
              classForm: { completePercent: 33, totalQuantity: 3, displayText: '1/3' },
              gpaForms: [
                {
                  isDone: false,
                  reviewStatus: 'REVIEWING',
                  displayGrade: '-',
                  formKey: 'gpaForm',
                  formTemplateId: '3001',
                  phaseTargetId: '102',
                  formTargetId: '5002',
                  formTemplateName: 'GPA 評量表單（第二階段）',
                  startDate: '2026/04/01',
                  dueDate: '2026/09/30',
                  flowList: [{ done: false, flowRoleName: '實習單位臨床教師', accountName: '陳中明' }],
                },
              ],
              epaForm: { completePercent: 25, totalQuantity: 4, displayText: '1/4' },
              epaForms: [
                {
                  formKey: 'epaForm',
                  formTemplateId: '6002',
                  phaseTargetId: '102',
                  formTargetId: '6002',
                  formTemplateName: 'EPA 評估表單（第二階段）',
                  displayGrade: '2',
                  startDate: '2026/04/01',
                  dueDate: '2026/09/30',
                  flowList: [{ done: false, flowRoleName: '實習單位臨床教師', accountName: '陳中明' }],
                },
              ],
            },
          ],
          studentName: '林同學',
          grade: 'C1',
          gpaLightColor: 'yellow',
          epaLightColor: 'yellow',
          gpaFailDeptNames: ['內科部'],
          epaFailDeptNames: ['內科部'],
          difficulty: true,
          difficulyReason: '實習出勤不穩定;需加強醫病溝通',
        },
        {
          planInst: { studentId: 'S0002', annualId: '1', organizationId: '1', jobTypeId: '1', planTargetId: '2' },
          fifthPlanTargetIdList: [],
          todoItemMap: {},
          mentors: [],
          clinicalTeachers: [],
          phaseList: [],
          studentName: '王同學',
          grade: 'C2',
          gpaLightColor: 'green',
          epaLightColor: 'green',
        },
        {
          // 張同學：有核心技能盒但無五年級計畫（teacherMain.js:166-172 的 alert 分支樣本）。
          planInst: { studentId: 'S0003', annualId: '1', organizationId: '1', jobTypeId: '1', planTargetId: '3' },
          fifthPlanTargetIdList: [],
          todoItemMap: {},
          mentors: [],
          clinicalTeachers: [],
          coreSkillDTO: {
            completedQuantity: 1,
            totalQuantity: 5,
            completePercent: 20,
            deptCoreSkills: [
              { displayText: '外科', completePercent: 20, completedQuantity: 1, totalQuantity: 5, expansion1: 'D02' },
            ],
          },
          phaseList: [
            {
              phaseTargetId: '103',
              phaseTargetName: '第一階段（外科）',
              currentPhase: true,
              showData: true,
              phaseDeptId: 'D02',
              phaseDeptName: '外科部',
              phasePeriod: '2026/04 - 2026/09',
              phasePercent: 60,
              startDate: '2026-04-01',
              endDate: '2026-09-30',
              planForm: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
              classForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
              gpaForms: [],
              epaForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
              epaForms: [],
            },
          ],
          studentName: '張同學',
          grade: 'C2',
          gpaLightColor: 'green',
          epaLightColor: 'green',
        },
      ],
      dentisit: [
        {
          planInst: { studentId: 'S0005', annualId: '1', organizationId: '2', jobTypeId: '2', planTargetId: '2' },
          fifthPlanTargetIdList: [],
          todoItemMap: {},
          mentors: [],
          clinicalTeachers: [],
          phaseList: [
            {
              phaseTargetId: '201',
              phaseTargetName: '牙醫第一階段（補經物）',
              currentPhase: true,
              showData: true,
              phaseDeptId: 'D11',
              phaseDeptName: '牙體復原科',
              phasePeriod: '2026/01 - 2026/06',
              phasePercent: 50,
              startDate: '2026-01-01',
              endDate: '2026-06-30',
              planForm: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
              classForm: { completePercent: 33, totalQuantity: 3, displayText: '1/3' },
              gpaForms: [],
              epaForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
              epaForms: [],
            },
          ],
          studentName: '牙醫學員',
          grade: 'C1',
          gpaLightColor: 'green',
          epaLightColor: 'green',
        },
      ],
      // MA 學員詳情（ma_studentPanel_tmpl：4 欄＋planForms modal＋無燈號）。
      ma: [
        {
          planInst: { studentId: 'S0006', annualId: '1', organizationId: '3', jobTypeId: '3', planTargetId: '3' },
          fifthPlanTargetIdList: [],
          todoItemMap: {},
          mentors: [],
          clinicalTeachers: [],
          phaseList: [
            {
              phaseTargetId: '301',
              phaseTargetName: '醫事第一階段（護理）',
              currentPhase: true,
              showData: true,
              phaseDeptId: 'D21',
              phaseDeptName: '護理部',
              phasePeriod: '2026/01 - 2026/06',
              phasePercent: 50,
              startDate: '2026-01-01',
              endDate: '2026-06-30',
              planForm: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
              classForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
              gpaForms: [],
              epaForm: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
              epaForms: [
                {
                  formKey: 'epaForm',
                  formTemplateId: '6101',
                  phaseTargetId: '301',
                  formTargetId: '6101',
                  formTemplateName: 'EPA 評估表單（護理 第一階段）',
                  startDate: '2026/01/01',
                  dueDate: '2026/06/30',
                  flowList: [{ done: true, flowRoleName: '實習單位臨床教師', accountName: '醫事臨床教師' }],
                  isDone: true,
                },
              ],
              planForms: [
                {
                  formKey: 'planForm',
                  formTemplateId: '3201',
                  phaseTargetId: '301',
                  formTargetId: '5201',
                  formTemplateName: '計畫表單（護理 第一階段）',
                  startDate: '2026/01/01',
                  dueDate: '2026/06/30',
                  flowList: [{ done: true, flowRoleName: '導師', accountName: '醫事導師' }],
                  isDone: true,
                },
              ],
              phaseScore: { score: '82' },
            },
          ],
          studentName: '醫事學員',
          grade: 'C1',
          gpaLightColor: 'green',
          epaLightColor: 'green',
        },
      ],
    },
    // 等第制成績分布報表（main/ugy/getStudentGPAFormReport；studentGPAGrade.js:119-127）。
    gpaFormReport: {
      gradeCountMap: { D: 0, C: 0, B: 1, 'A-': 0, A: 1, 'A+': 0, 'A++': 0 },
      avgScore: '85.0',
      allStudentAvgScore: '82.3',
      gpaForms: [
        {
          formTemplateName: 'GPA 評量表單（第一階段）',
          planTargetName: 'UGY 計畫',
          subPhaseName: '第一階段',
          phaseName: '',
          phaseDepartmentName: '內科部',
          phaseStartDate: '2026/01/01',
          phaseEndDate: '2026/03/31',
          dueDate: '2026/03/31',
          flowList: [{ done: true, flowRoleName: '實習單位臨床教師', accountName: '李大明' }],
          displayGrade: 'A',
          formKey: 'gpaForm',
          formTemplateId: '3001',
          phaseTargetId: '101',
          formTargetId: '5001',
        },
        {
          formTemplateName: 'GPA 評量表單（第二階段）',
          planTargetName: 'UGY 計畫',
          subPhaseName: '第二階段',
          phaseName: '',
          phaseDepartmentName: '外科部',
          phaseStartDate: '2026/04/01',
          phaseEndDate: '2026/09/30',
          dueDate: '2026/09/30',
          flowList: [{ done: true, flowRoleName: '實習單位臨床教師', accountName: '李大明' }],
          displayGrade: 'B',
          formKey: 'gpaForm',
          formTemplateId: '3001',
          phaseTargetId: '102',
          formTargetId: '5002',
        },
      ],
    },
    // EPA 成績分布報表（main/ugy/getStudentEPAFormReport；studentEpaGrade.js:143-167）。
    epaFormReport: {
      epaGradeMap: {
        '溝通與人際關係': {
          scoreCountMap: { '1.0': 0, '2.0': 1, '3.0': 2, '4.0': 1, '5.0': 0 },
          testingCount: 4,
          studentAvgScore: 3,
          avgScore: 3.2,
        },
        '病人照護': {
          scoreCountMap: { '1.0': 0, '2.0': 0, '3.0': 1, '4.0': 2, '5.0': 1 },
          testingCount: 4,
          studentAvgScore: 4,
          avgScore: 3.8,
        },
      },
      epaTitleMap: {
        '溝通與人際關係': '與病人/家屬及醫療團隊成員之溝通與人際關係',
        '病人照護': '建立並維持良好的醫病關係，提供以病人為中心的照護',
      },
      epaForms: [
        {
          form: {
            formTemplateName: 'EPA 評估表單（第一階段）',
            planTargetName: 'UGY 計畫',
            subPhaseName: '第一階段',
            phaseName: '',
            phaseDepartmentName: '內科部',
            phaseStartDate: '2026/01/01',
            phaseEndDate: '2026/03/31',
            dueDate: '2026/03/31',
            flowList: [{ done: true, flowRoleName: '實習單位臨床教師', accountName: '李大明' }],
            displayGrade: '3',
            formKey: 'epaForm',
            formTemplateId: '6001',
            phaseTargetId: '101',
            formTargetId: '6001',
          },
          epaScoreMap: { '溝通與人際關係': 3, '病人照護': 4 },
        },
        {
          form: {
            formTemplateName: 'EPA 評估表單（第二階段）',
            planTargetName: 'UGY 計畫',
            subPhaseName: '第二階段',
            phaseName: '',
            phaseDepartmentName: '外科部',
            phaseStartDate: '2026/04/01',
            phaseEndDate: '2026/09/30',
            dueDate: '2026/09/30',
            flowList: [{ done: true, flowRoleName: '實習單位臨床教師', accountName: '李大明' }],
            displayGrade: '3',
            formKey: 'epaForm',
            formTemplateId: '6002',
            phaseTargetId: '102',
            formTargetId: '6002',
          },
          epaScoreMap: { '溝通與人際關係': 2, '病人照護': 3 },
        },
      ],
    },
    // 公告內容（announcement/getAnnouncementAjax；modal/announce.jsp:88-108）。
    announcementDetails: {
      '1': {
        id: '1',
        subject: '系統維護公告（09/28 22:00-24:00）',
        startDateStr: '2026/09/25',
        endDateStr: '2026/09/28',
        refName: '教學單位',
        roleNames: '全部角色',
        url: '',
        content: '系統將於 09/28 22:00-24:00 進行例行性維護，期間暫停服務。',
      },
      '2': {
        id: '2',
        subject: '115 年度第一梯次實習說明會',
        startDateStr: '2026/09/24',
        endDateStr: '2026/10/15',
        refName: '教學單位',
        roleNames: '學員',
        url: 'https://example.org/internship-115-1',
        content: '115 年度第一梯次實習說明會將於 10 月舉行，請學員準時參加。',
      },
      '3': {
        id: '3',
        subject: '線上測驗題庫更新通知',
        startDateStr: '2026/09/20',
        endDateStr: '2026/09/30',
        refName: '教學單位',
        roleNames: '全部角色',
        url: '',
        content: '題庫已完成年度更新，請各單位於測驗前重新確認題目版本。',
      },
      '4': {
        id: '4',
        subject: '教學檢討會議程預告',
        startDateStr: '2026/09/18',
        endDateStr: '2026/09/26',
        refName: '教學單位',
        roleNames: '教師',
        url: '',
        content: '教學檢討會將於本週五舉行，議程已公告，請與會人員先行閱覽。',
      },
    },
    // 待填表單（ajax/todo/getMyTodoListAjax；modal/todoForm.jsp:16-110 的四種分流樣本）。
    todoForms: [
      {
        id: 't1',
        formTemplateName: 'GPA+評量表單',
        formTargetTypeName: '第一階段',
        refTable: 'PhaseTarget',
        refId: '101',
        formKey: 'gpaForm',
        formTemplateId: '3001',
        formTargetId: '5001',
        studentName: '林同學',
        startDate: '2026/09/01',
        dueDate: '2026/09/30',
        planName: '115 年度 UGY 計畫',
        phaseName: '第一階段（內科）',
      },
      {
        id: 't2',
        formTemplateName: '請假單',
        kind: 'leave',
        refTable: 'LeaveForm',
        refId: '7001',
        formKey: 'leaveForm',
        formTemplateId: '4001',
        formTargetId: '7001',
        studentName: '林同學',
        startDate: '2026/09/20',
        dueDate: '2026/09/27',
      },
      {
        id: 't3',
        formTemplateName: '學習紀錄簽核',
        refTable: 'studyRecord',
        refId: '8001',
        formKey: 'studyRecord',
        formTemplateId: '0',
        formTargetId: '8001',
        studentName: '林同學',
        startDate: '2026/09/15',
        dueDate: '2026/09/25',
      },
      {
        id: 't4',
        formTemplateName: '線上測驗批改',
        refTable: 'examList',
        refId: '9001',
        formKey: 'examList',
        formTemplateId: '0',
        formTargetId: '9001',
        studentName: '林同學',
        startDate: '2026/09/10',
        dueDate: '2026/09/24',
      },
    ],
    // 臨床核心技能表單（form/getCoreSkillForm；modal/coreSkillForms.jsp:50-103）。
    coreSkillForms: [
      {
        id: 1,
        formTemplateName: '核心技能：病史詢問與身體評估',
        isDone: true,
        flowList: [
          { done: true, flowRoleName: '實習單位臨床教師', accountName: '李大明' },
          { done: true, flowRoleName: '導師', accountName: '王大同' },
        ],
        displayGrade: 'A',
        score: 10,
        formKey: 'coreSkillForm',
        formTemplateId: '5001',
        phaseTargetId: '101',
        formTargetId: '5101',
      },
      {
        id: 2,
        formTemplateName: '核心技能：醫療品質與病人安全',
        isDone: false,
        flowList: [{ done: false, flowRoleName: '實習單位臨床教師', accountName: '陳中明' }],
        displayGrade: '-',
        score: 9,
        formKey: 'coreSkillForm',
        formTemplateId: '5002',
        phaseTargetId: '101',
        formTargetId: '5102',
      },
      {
        id: 0,
        formTemplateName: '核心技能：跨領域團隊合作',
        isDone: false,
        flowList: [],
        displayGrade: '-',
        score: 8,
        formKey: 'coreSkillForm',
        formTemplateId: '5003',
        phaseTargetId: '102',
        formTargetId: '5103',
      },
    ],
    // 個人行程明細（mySchedule/getMyScheduleAjax；addCalendarModal.jsp:73-96）——對應事件 c3。
    mySchedules: [
      {
        id: 'c3',
        content: '個人行程：圖書館',
        scheduleStartDateString: '2026/09/28 08:00',
        scheduleEndDateString: '2026/09/28 12:00',
        memo: '借閱臨床醫學參考書。',
        groupKey: '',
      },
    ],
    // ── 批次 20：科部管理員首頁（main/ugy/deptAdmin/*；deptAdminMain.js）──
    // 科部管理員首頁資料（renderMenu：ALL_FORM 待填表單盒；planList_Box_Tmpl 訓練計畫盒）。
    deptAdminMainDto: {
      todoItemMap: { ALL_FORM: { incompleteQuantity: 2 } },
      planTargetList: [
        { id: '1', name: '115 年度 UGY 計畫' },
        { id: '2', name: '115 年度 UGY 計畫 B' },
        { id: '3', name: '115 年度 UGY 計畫 C' },
      ],
    },
    // 計畫資料（queryPlanInfo）：計畫 1＝3 階段全量（clinicalAmount=3）；計畫 2＝空 phaseList
    // （deptAdminMain.js:221「此計畫尚未安排學員！」（genPlanPanel :208-224）分支樣本）。
    deptAdminPlans: {
      '1': {
        planTargetId: '1',
        planTarget: { id: '1', name: '115 年度 UGY 計畫', annualId: '1', organizationId: '1', jobTypeId: '1', clinicalAmount: 3 },
        phaseList: [
          {
            phaseTargetId: 'pt1',
            phaseTargetName: '第一階段（內科）',
            phaseDeptId: '1',
            phaseDeptName: '內科部',
            phasePeriod: '2026/01 - 2026/03',
            startDate: '2026-01-01',
            endDate: '2026-03-31',
            phasePercent: 100,
            currentPhase: false,
            showData: true,
            planForm: { completePercent: 100, totalQuantity: 2, displayText: '2/2' },
            classAndMeetingForm: { completePercent: 100, totalQuantity: 3, displayText: '3/3' },
            clinicalArrangement: { completePercent: 100, totalQuantity: 2, displayText: '2/2' },
          },
          {
            phaseTargetId: 'pt2',
            phaseTargetName: '第二階段（外科）',
            phaseDeptId: '2',
            phaseDeptName: '外科部',
            phasePeriod: '2026/04 - 2026/09',
            startDate: '2026-04-01',
            endDate: '2026-09-30',
            phasePercent: 60,
            currentPhase: true,
            showData: true,
            planForm: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
            classAndMeetingForm: { completePercent: 33, totalQuantity: 3, displayText: '1/3' },
            clinicalArrangement: { completePercent: 50, totalQuantity: 2, displayText: '1/2' },
          },
          {
            phaseTargetId: 'pt3',
            phaseTargetName: '第三階段（急診）',
            phaseDeptId: '3',
            phaseDeptName: '急診醫學部',
            phasePeriod: '2026/10 - 2026/12',
            startDate: '2026-10-01',
            endDate: '2026-12-31',
            phasePercent: 0,
            currentPhase: false,
            showData: false,
            planForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            classAndMeetingForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            clinicalArrangement: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
          },
        ],
      },
      '2': {
        planTargetId: '2',
        planTarget: { id: '2', name: '115 年度 UGY 計畫 B', annualId: '1', organizationId: '1', jobTypeId: '1', clinicalAmount: 2 },
        phaseList: [],
      },
      // 計畫 3＝clinicalAmount 2 樣本（modal 第 4 欄隱藏＋驗證訊息只拼到臨床教師2；
      // deptAdminMain.js:367-372/:191-198）。
      '3': {
        planTargetId: '3',
        planTarget: { id: '3', name: '115 年度 UGY 計畫 C', annualId: '1', organizationId: '1', jobTypeId: '1', clinicalAmount: 2 },
        phaseList: [
          {
            phaseTargetId: 'pt4',
            phaseTargetName: '第一階段（內科）',
            phaseDeptId: '1',
            phaseDeptName: '內科部',
            phasePeriod: '2026/02 - 2026/04',
            startDate: '2026-02-01',
            endDate: '2026-04-30',
            phasePercent: 40,
            currentPhase: false,
            showData: true,
            planForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            classAndMeetingForm: { completePercent: 0, totalQuantity: 0, displayText: '0/0' },
            clinicalArrangement: { completePercent: 0, totalQuantity: 1, displayText: '0/1' },
          },
        ],
      },
    },
    // 隨段學員臨床教師（getPhaseClinicalList）：pt1 兩員全安排（2/2）；pt2 一員未安排（1/2）；pt3 無。
    deptAdminClinicalLists: {
      pt1: [
        { phaseInstId: 'pi1', studentName: '林同學', studentEmpId: 'S0001', clinical1Id: 7, clinical2Id: 8, clinical3Id: 9 },
        { phaseInstId: 'pi2', studentName: '王同學', studentEmpId: 'S0002', clinical1Id: 7, clinical2Id: 8, clinical3Id: 9 },
      ],
      pt2: [
        { phaseInstId: 'pi3', studentName: '林同學', studentEmpId: 'S0001', clinical1Id: 7, clinical2Id: 8, clinical3Id: 9 },
        { phaseInstId: 'pi4', studentName: '張同學', studentEmpId: 'S0003', clinical1Id: 0, clinical2Id: 0, clinical3Id: 0 },
      ],
      pt3: [],
      pt4: [
        { phaseInstId: 'pi5', studentName: '王同學', studentEmpId: 'S0002', clinical1Id: 0, clinical2Id: 0, clinical3Id: 0 },
      ],
    },
    // 部門清單（ajax/getDepartmentListByJobTypeAjax；organizationId=1＋jobTypeId=1 的清單）。
    deptAdminDepartments: [
      { id: '1', name: '內科部' },
      { id: '2', name: '外科部' },
      { id: '3', name: '急診醫學部' },
    ],
    // 部門帳號清單（ajax/getAccountListByDepartmentIdNewAjax；key＝departmentId；id＝帳號 id 數值字串）。
    // 登入部門 '1' 的清單含 7/8/9：已指派帳號（clinical1Id~3Id）都在清單內＝初始還原成功
    // （若不在，legacy val() no-op＝請選擇，遺留待重指——本種子取可用樣本）。
    deptAdminAccounts: {
      '1': [
        { id: '7', name: '李大明' },
        { id: '8', name: '陳中明' },
        { id: '9', name: '王大同' },
      ],
      '2': [{ id: '8', name: '陳中明' }],
      '3': [{ id: '7', name: '李大明' }],
    },
    // 登入帳號所屬部門（layout server 變數；dept select 預設值，deptAdminMain.js:379）。
    loginAccountDepartmentId: '1',
    // 行事曆 class/meeting 事件對應課程列（CourseViewModal 的 row；批次 11 的欄位形狀）。
    courseRows: [
      {
        id: 'c1',
        name: '內科晨會',
        category2: '晨會',
        category3: '內科部',
        date: '2026/09/28',
        beginTime: '08:00',
        endTime: '09:00',
        locationId: 'L01',
        qty: 30,
        attendanceQty: 0,
        attendance: '',
        isSignUp: '0',
        isCrossDomain: '0',
        kind: 'class',
        jobTypeId: '1',
        refJobTypeId: '1',
        formOwnerName: '李大明',
        classTeacherList: [],
        announcements: [],
        attachFiles: [],
        forms: [],
        actionType: '',
      },
      {
        id: 'c2',
        name: '教學檢討會',
        category2: '檢討會',
        category3: '教學單位',
        date: '2026/09/29',
        beginTime: '13:30',
        endTime: '15:30',
        locationId: 'L02',
        qty: 50,
        attendanceQty: 0,
        attendance: '',
        isSignUp: '0',
        isCrossDomain: '0',
        kind: 'meeting',
        jobTypeId: '1',
        refJobTypeId: '1',
        formOwnerName: '王大同',
        classTeacherList: [],
        announcements: [],
        attachFiles: [],
        forms: [],
        actionType: '',
      },
    ],
  };
}

// ---------------------------------------------------------------------------
// 持久化（批次 0 同款：key 掛在 mock-data 前綴之下，重置一併清掉）
// ---------------------------------------------------------------------------

const DASHBOARD_STORAGE_PREFIX = 'eportfolio-ui.mock-data.dashboard';

/** key 含 scenario（批次 0 同理由：不同 scenario 的髒資料不互染）。 */
function dashboardStorageKey(scenario: string, name: string): string {
  return `${DASHBOARD_STORAGE_PREFIX}.${scenario}.${name}`;
}

/**
 * 讀取（讀不到／格式不對＝null，呼叫端退回種子；批次 0 readPersistedResource 同款降級：
 * 隱私視窗／配額爆掉不提示）。
 */
export function readPersistedDashboard(scenario: string, name: string): unknown | null {
  try {
    const raw = window.localStorage.getItem(dashboardStorageKey(scenario, name));
    if (raw === null) return null;
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

/** 寫入（失敗不提示，只影響跨重新整理；批次 0 writePersistedResource 同款）。 */
export function writePersistedDashboard(scenario: string, name: string, value: unknown): void {
  try {
    window.localStorage.setItem(dashboardStorageKey(scenario, name), JSON.stringify(value));
  } catch {
    // 降級：只在記憶體內生效。
  }
}

/**
 * scenario 閘門（MockDataSource.gate() 同語意）：`error` scenario 連 dashboard 讀取都擋，
 * 讓「載入就失敗」的路徑真的有機會被按到。訊息用 mainUtils.js 的共用錯誤字面值
 * （queryCoreSkillForm/getStudentGPAFormReport/getStudentEPAFormReport 的 error handler
 * 都是 `showAlertDialog("查詢失敗")`，mainUtils.js:528/577/601）。
 */
export function dashboardGate(scenario: string): void {
  if (scenario === 'error') {
    throw new MockDataError('error', '查詢失敗');
  }
}

/**
 * 變更種子（讀取 → mutate → 寫回持久層）；讀不到時以種子重建。呼叫端以回傳值更新 state
 * （資料不重新載入，等價 legacy 的 refetchEvents／表格 redraw 後的畫面）。
 */
/**
 * 批次 20：讀取＋淺合併（舊持久層（批次 19 寫入）缺批次 20 deptAdmin 欄位時以種子補齊；
 * 新欄位以持久值為準）。DashboardBody／DeptAdminBody 的載入端共用。
 */
export function readDashboardData(scenario: string, now: Date): { data: DashboardData; fresh: boolean } {
  const persisted = readPersistedDashboard(scenario, 'data') as DashboardData | null;
  if (persisted === null) {
    const seeds = buildDefaultDashboardData(now);
    writePersistedDashboard(scenario, 'data', seeds);
    return { data: seeds, fresh: true };
  }
  return { data: { ...buildDefaultDashboardData(now), ...persisted }, fresh: false };
}

export function updateDashboardData(
  scenario: string,
  mutate: (data: DashboardData) => DashboardData,
  now: Date,
): DashboardData {
  const persisted = readPersistedDashboard(scenario, 'data') as DashboardData | null;
  const current =
    persisted === null ? buildDefaultDashboardData(now) : { ...buildDefaultDashboardData(now), ...persisted };
  const next = mutate(current);
  writePersistedDashboard(scenario, 'data', next);
  return next;
}
